import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mail, Check, X, Loader2, Trash2, RefreshCw, AtSign, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approuvé', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-700' },
};

export default function EmailRequestsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [decisionTarget, setDecisionTarget] = useState(null); // { item, action: 'approve'|'reject' }
  const [adminNotes, setAdminNotes] = useState('');
  const [assignedEmail, setAssignedEmail] = useState('');
  const [initialPassword, setInitialPassword] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const res = await axios.get(`${API}/admin/email-requests`, { params, withCredentials: true });
      setItems(res.data || []);
    } catch (e) {
      console.error('Failed to load email requests:', e);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openDecision = (item, action) => {
    setDecisionTarget({ item, action });
    setAdminNotes('');
    setAssignedEmail(action === 'approve' ? `${item.desired_username}@cresa-asbl.org` : '');
    setInitialPassword('');
  };

  // Suggest a strong, memorable initial password
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let s = '';
    for (let i = 0; i < 12; i++) s += chars[Math.floor(Math.random() * chars.length)];
    return `Cresa${s}!`;
  };

  const submitDecision = async () => {
    if (!decisionTarget) return;
    if (decisionTarget.action === 'approve' && (!initialPassword || initialPassword.length < 8)) {
      toast.error("Mot de passe initial requis (8 caractères minimum).");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        status: decisionTarget.action === 'approve' ? 'approved' : 'rejected',
        admin_notes: adminNotes,
        ...(decisionTarget.action === 'approve' ? {
          assigned_email: assignedEmail,
          initial_password: initialPassword,
        } : {}),
      };
      await axios.post(
        `${API}/admin/email-requests/${decisionTarget.item.email_request_id}/decision`,
        payload,
        { withCredentials: true }
      );
      toast.success(
        decisionTarget.action === 'approve'
          ? 'Demande approuvée. Compte créé et notification envoyée.'
          : 'Demande rejetée. Le requérant a été notifié.'
      );
      setDecisionTarget(null);
      fetchItems();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur lors de la décision';
      toast.error(typeof detail === 'string' ? detail : 'Erreur inattendue');
    } finally {
      setBusy(false);
    }
  };

  const submitResetPassword = async () => {
    if (!resetTarget) return;
    if (!resetPassword || resetPassword.length < 8) {
      toast.error('Mot de passe requis (8 caractères minimum).');
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/email-requests/${resetTarget.email_request_id}/reset-password`,
        { new_password: resetPassword },
        { withCredentials: true }
      );
      toast.success('Mot de passe réinitialisé. Le titulaire a été notifié.');
      setResetTarget(null);
      setResetPassword('');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Erreur lors de la réinitialisation';
      toast.error(typeof detail === 'string' ? detail : 'Erreur inattendue');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette demande ?')) return;
    try {
      await axios.delete(`${API}/admin/email-requests/${id}`, { withCredentials: true });
      toast.success('Demande supprimée');
      fetchItems();
    } catch (e) {
      console.error(e);
      toast.error('Erreur de suppression');
    }
  };

  const counts = {
    pending: items.filter((i) => i.status === 'pending').length,
    approved: items.filter((i) => i.status === 'approved').length,
    rejected: items.filter((i) => i.status === 'rejected').length,
  };

  return (
    <div data-testid="email-requests-manager">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Demandes d&apos;adresses e-mail CRESA
          </h2>
          <p className="text-sm text-[#5C665A] mt-1">
            Examinez et validez les demandes d&apos;attribution d&apos;adresses <code>@cresa-asbl.org</code>.
          </p>
        </div>
        <button onClick={fetchItems} className="btn-secondary inline-flex items-center gap-2" data-testid="refresh-email-requests">
          <RefreshCw size={16} /> Rafraîchir
        </button>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <button key={s}
            onClick={() => setFilter(s)}
            data-testid={`email-req-filter-${s}`}
            className={`px-4 py-2 rounded-full text-sm font-bold border-2 transition-all ${
              filter === s
                ? 'bg-[#4A6B48] border-[#4A6B48] text-white'
                : 'border-[#E5E3DB] text-[#5C665A] hover:border-[#4A6B48] hover:text-[#4A6B48]'
            }`}>
            {s === 'all' ? `Toutes (${items.length})` : `${STATUS_LABELS[s].label} (${counts[s] || 0})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#4A6B48]" size={32} /></div>
      ) : items.length === 0 ? (
        <p className="text-center text-[#5C665A] py-12" data-testid="email-requests-empty">
          Aucune demande {filter !== 'all' ? `(${STATUS_LABELS[filter]?.label.toLowerCase()})` : ''} pour le moment.
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((req) => {
            const status = STATUS_LABELS[req.status] || STATUS_LABELS.pending;
            return (
              <article key={req.email_request_id}
                className="card border-l-4 border-[#4A6B48]"
                data-testid={`email-req-card-${req.email_request_id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded uppercase ${status.color}`}>
                        {status.label}
                      </span>
                      <span className="text-xs text-[#5C665A] inline-flex items-center gap-1">
                        <Calendar size={11} />{new Date(req.created_at).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-[#2E332D]">{req.full_name}</h3>
                    <p className="text-sm text-[#5C665A]">{req.position}{req.department ? ` — ${req.department}` : ''}</p>
                    <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-sm">
                      <div className="inline-flex items-center gap-1.5 text-[#5C665A]">
                        <Mail size={13} />
                        <a href={`mailto:${req.personal_email}`} className="text-[#4A6B48] hover:underline">
                          {req.personal_email}
                        </a>
                      </div>
                      {req.phone && (
                        <div className="text-[#5C665A]">📞 {req.phone}</div>
                      )}
                      {req.date_of_birth && (
                        <div className="text-[#5C665A]">🎂 {new Date(req.date_of_birth).toLocaleDateString('fr-FR')}</div>
                      )}
                      <div className="inline-flex items-center gap-1.5 text-[#5C665A] sm:col-span-2">
                        <AtSign size={13} />
                        <span className="font-mono font-bold text-[#2E332D]">
                          {req.assigned_email || `${req.desired_username}@cresa-asbl.org`}
                        </span>
                        {req.account_created && (
                          <span className="text-[10px] bg-[#4A6B48] text-white px-1.5 py-0.5 rounded font-bold ml-1">
                            Compte actif
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#E5E3DB]">
                      <p className="text-xs font-bold text-[#5C665A] uppercase mb-1 inline-flex items-center gap-1">
                        <FileText size={11} /> Justification
                      </p>
                      <p className="text-sm text-[#2E332D] whitespace-pre-line">{req.justification}</p>
                    </div>
                    {req.admin_notes && (
                      <div className="mt-2 bg-[#F9F8F6] p-2 rounded text-xs">
                        <strong className="text-[#4A6B48]">Note admin :</strong> {req.admin_notes}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 shrink-0">
                    {req.status === 'pending' && (
                      <>
                        <button onClick={() => openDecision(req, 'approve')}
                          data-testid={`approve-email-req-${req.email_request_id}`}
                          className="inline-flex items-center gap-1 bg-[#4A6B48] text-white text-sm font-bold px-3 py-2 rounded hover:bg-[#3B5539]">
                          <Check size={14} /> Approuver
                        </button>
                        <button onClick={() => openDecision(req, 'reject')}
                          data-testid={`reject-email-req-${req.email_request_id}`}
                          className="inline-flex items-center gap-1 border-2 border-red-500 text-red-500 text-sm font-bold px-3 py-2 rounded hover:bg-red-50">
                          <X size={14} /> Rejeter
                        </button>
                      </>
                    )}
                    {req.status === 'approved' && (
                      <button onClick={() => { setResetTarget(req); setResetPassword(generatePassword()); }}
                        data-testid={`reset-pw-email-req-${req.email_request_id}`}
                        className="inline-flex items-center gap-1 border-2 border-[#D87D4A] text-[#D87D4A] text-sm font-bold px-3 py-2 rounded hover:bg-[#D87D4A] hover:text-white">
                        🔐 Réinitialiser mot de passe
                      </button>
                    )}
                    <button onClick={() => handleDelete(req.email_request_id)}
                      data-testid={`delete-email-req-${req.email_request_id}`}
                      className="inline-flex items-center gap-1 text-xs text-[#5C665A] hover:text-red-500 px-2 py-1">
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Decision modal */}
      {decisionTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 9999 }} data-testid="decision-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {decisionTarget.action === 'approve' ? 'Approuver la demande' : 'Rejeter la demande'}
            </h3>
            <p className="text-sm text-[#5C665A] mb-4">
              Demande de <strong>{decisionTarget.item.full_name}</strong> ({decisionTarget.item.personal_email})
            </p>
            {decisionTarget.action === 'approve' && (
              <>
                <div className="mb-4">
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">Adresse à assigner</label>
                  <input type="email" value={assignedEmail}
                    onChange={(e) => setAssignedEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-[#E5E3DB] rounded font-mono text-sm"
                    data-testid="decision-assigned-email-input" />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-bold text-[#2E332D] mb-1">
                    Mot de passe initial * <span className="text-xs font-normal text-[#5C665A]">(communiqué au demandeur)</span>
                  </label>
                  <div className="flex gap-2">
                    <input type="text" value={initialPassword}
                      onChange={(e) => setInitialPassword(e.target.value)}
                      minLength={8}
                      placeholder="Au moins 8 caractères"
                      className="flex-1 px-3 py-2 border border-[#E5E3DB] rounded font-mono text-sm"
                      data-testid="decision-initial-password-input" />
                    <button type="button"
                      onClick={() => setInitialPassword(generatePassword())}
                      data-testid="decision-generate-password"
                      className="text-xs font-bold px-3 py-2 rounded border-2 border-[#4A6B48] text-[#4A6B48] hover:bg-[#4A6B48] hover:text-white whitespace-nowrap">
                      🎲 Générer
                    </button>
                  </div>
                  <p className="text-xs text-[#5C665A] mt-1">
                    Ce mot de passe sera envoyé au requérant par e-mail. Il pourra le modifier après sa première connexion.
                  </p>
                </div>
              </>
            )}
            <div className="mb-4">
              <label className="block text-sm font-bold text-[#2E332D] mb-1">
                {decisionTarget.action === 'approve' ? 'Note (optionnel)' : 'Motif du rejet (recommandé)'}
              </label>
              <textarea rows={3} value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder={decisionTarget.action === 'approve'
                  ? "Ex. : Vos identifiants suivront sous 48h."
                  : "Ex. : Justification insuffisante. Recontactez-nous avec plus de détails."}
                className="w-full px-3 py-2 border border-[#E5E3DB] rounded text-sm"
                data-testid="decision-notes-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setDecisionTarget(null)}
                className="btn-secondary" disabled={busy}>
                Annuler
              </button>
              <button onClick={submitDecision} disabled={busy}
                data-testid="confirm-decision-button"
                className={`inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-bold text-white disabled:opacity-50 ${
                  decisionTarget.action === 'approve' ? 'bg-[#4A6B48] hover:bg-[#3B5539]' : 'bg-red-500 hover:bg-red-600'
                }`}>
                {busy ? <Loader2 className="animate-spin" size={14} /> : (decisionTarget.action === 'approve' ? <Check size={14} /> : <X size={14} />)}
                {busy ? 'Envoi…' : (decisionTarget.action === 'approve' ? 'Confirmer l\'approbation' : 'Confirmer le rejet')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 9999 }} data-testid="reset-password-modal">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 shadow-2xl">
            <h3 className="text-xl font-bold text-[#2E332D] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Réinitialiser le mot de passe
            </h3>
            <p className="text-sm text-[#5C665A] mb-4">
              Compte : <strong className="font-mono">{resetTarget.assigned_email}</strong> ({resetTarget.full_name})
            </p>
            <div className="mb-4">
              <label className="block text-sm font-bold text-[#2E332D] mb-1">Nouveau mot de passe *</label>
              <div className="flex gap-2">
                <input type="text" value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  minLength={8}
                  className="flex-1 px-3 py-2 border border-[#E5E3DB] rounded font-mono text-sm"
                  data-testid="reset-password-input" />
                <button type="button"
                  onClick={() => setResetPassword(generatePassword())}
                  data-testid="reset-generate-password"
                  className="text-xs font-bold px-3 py-2 rounded border-2 border-[#4A6B48] text-[#4A6B48] hover:bg-[#4A6B48] hover:text-white">
                  🎲 Générer
                </button>
              </div>
              <p className="text-xs text-[#5C665A] mt-1">
                ⚠️ Toutes les sessions actives du titulaire seront déconnectées. Le nouveau mot de passe sera envoyé par e-mail.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setResetTarget(null); setResetPassword(''); }}
                className="btn-secondary" disabled={busy}>
                Annuler
              </button>
              <button onClick={submitResetPassword} disabled={busy || !resetPassword || resetPassword.length < 8}
                data-testid="confirm-reset-button"
                className="inline-flex items-center gap-1 bg-[#D87D4A] text-white px-4 py-2 rounded text-sm font-bold hover:bg-[#C56D3F] disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={14} /> : '🔐'}
                {busy ? 'Envoi…' : 'Confirmer la réinitialisation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
